<template>
  <div v-if="visible" class="modal-overlay" @click.self="close">
    <div class="modal-container glass-modal-container modal-email">
      <div class="modal-header glass-modal-header">
        <h3>Send Email to Finance Company</h3>
        <button class="modal-close" @click="close">×</button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label>Select Email Template</label>
          <div class="template-selector">
            <button 
              v-for="template in templates" 
              :key="template.id"
              :class="['template-btn glass-btn', { active: selectedTemplateId === template.id }]"
              @click="selectTemplate(template.id)"
            >
              {{ template.name }}
            </button>
          </div>
        </div>

        <div class="info-card glass-card-inner" v-if="financeCompany">
          <h4>Finance Company: {{ financeCompany.finance_company_name }}</h4>
          <div class="editable-field">
            <p>
              <strong>Email:</strong> 
              <span v-if="!editingEmail" class="email-display">
                {{ financeCompany.email || 'Not available' }}
                <button @click="startEditEmail" class="edit-email-btn" title="Edit email address">
                  ✏️
                </button>
              </span>
              <span v-else class="email-edit-mode">
                <input 
                  type="email" 
                  v-model="editableEmail" 
                  class="email-input glass-input"
                  placeholder="Enter email address..."
                  @keyup.enter="saveEmail"
                  @keyup.esc="cancelEditEmail"
                  autofocus
                />
                <button @click="saveEmail" class="save-email-btn" title="Save">✓</button>
                <button @click="cancelEditEmail" class="cancel-email-btn" title="Cancel">✗</button>
              </span>
            </p>
            <p><strong>Phone:</strong> {{ financeCompany.phone_number || 'Not available' }}</p>
          </div>
        </div>

        <div class="form-group">
          <label>Email Subject</label>
          <input type="text" v-model="subject" class="modal-input glass-input" placeholder="Enter email subject..." />
        </div>

        <div class="form-group">
          <label>Email Body (Editable)</label>
          <div class="email-editor-toolbar">
            <button type="button" @click="formatText('bold')" class="toolbar-btn" title="Bold"><b>B</b></button>
            <button type="button" @click="formatText('italic')" class="toolbar-btn" title="Italic"><i>I</i></button>
            <button type="button" @click="formatText('underline')" class="toolbar-btn" title="Underline"><u>U</u></button>
            <button type="button" @click="insertPlaceholder('{dispute_id}')" class="toolbar-btn">[Dispute ID]</button>
            <button type="button" @click="insertPlaceholder('{invoice_id}')" class="toolbar-btn">[Invoice #]</button>
            <button type="button" @click="insertPlaceholder('{customer_name}')" class="toolbar-btn">[Customer]</button>
            <button type="button" @click="insertPlaceholder('{issue_type}')" class="toolbar-btn">[Issue Type]</button>
            <button type="button" @click="insertPlaceholder('{dispute_details}')" class="toolbar-btn">[Dispute Details]</button>
            <button type="button" @click="insertPlaceholder('{customer_statement}')" class="toolbar-btn">[Statement]</button>
          </div>
          <textarea ref="bodyEditor" v-model="body" rows="8" class="modal-textarea glass-input email-body-textarea" placeholder="Write your email here..."></textarea>
        </div>

        <!-- Evidence Selection Section -->
        <div class="form-group evidence-selection-group">
          <label class="section-label">📎 Select Information to Include in PDF Attachment:</label>
          <div class="evidence-options-grid">
            <label class="evidence-checkbox">
              <input type="checkbox" v-model="selectedEvidence.customerInfo" />
              <span class="checkbox-content">👤 Customer Information</span>
            </label>
            
            <label class="evidence-checkbox">
              <input type="checkbox" v-model="selectedEvidence.orderItems" />
              <span class="checkbox-content">📦 Order Items ({{ orderItemsCount }})</span>
            </label>
            
            <label class="evidence-checkbox">
              <input type="checkbox" v-model="selectedEvidence.activityTimeline" />
              <span class="checkbox-content">📅 Activity Timeline ({{ timelineEventsCount }} events)</span>
            </label>
            
            <label class="evidence-checkbox">
              <input type="checkbox" v-model="selectedEvidence.deliveryInfo" />
              <span class="checkbox-content">🚚 Delivery Information</span>
            </label>
            
            <label class="evidence-checkbox">
              <input type="checkbox" v-model="selectedEvidence.routeInfo" />
              <span class="checkbox-content">🗺️ Route Information</span>
            </label>
            
            <label class="evidence-checkbox">
              <input type="checkbox" v-model="selectedEvidence.deliveryProof" />
              <span class="checkbox-content">📸 Delivery Proof</span>
            </label>
          </div>
          
          <div class="selection-actions">
            <button type="button" @click="selectAllEvidence" class="action-link">✓ Select All</button>
            <button type="button" @click="deselectAllEvidence" class="action-link">✗ Deselect All</button>
          </div>
        </div>

        <!-- Image Attachments Option -->
        <div class="form-group image-attachments-group">
          <label class="section-label">🖼️ Image Attachments:</label>
          <div class="attachment-options">
            <label class="checkbox-label">
              <input type="checkbox" v-model="attachImagesAsAttachments" />
              <span>Attach delivery images as separate files</span>
            </label>
            <p class="file-hint">📎 Images will be attached as JPEG/PNG files along with the PDF report</p>
            
            <div v-if="attachImagesAsAttachments && getDeliveryImages().length > 0" class="image-preview-list">
              <div v-for="(img, idx) in getDeliveryImages()" :key="idx" class="preview-item">
                <img :src="getAbsoluteUrl(img.image_path || img.url)" class="preview-thumb" @error="handleImageError" />
                <span class="preview-name">Image {{ idx + 1 }}</span>
              </div>
            </div>
            <div v-else-if="attachImagesAsAttachments && getDeliveryImages().length === 0" class="no-images-msg">
              ⚠️ No delivery images found for this order
            </div>
          </div>
        </div>

        <div class="form-group">
          <label class="checkbox-label">
            <input type="checkbox" v-model="attachDisputeInfo" />
            <span>Attach Dispute Information Document (PDF)</span>
          </label>
          <p class="file-hint">📄 This will generate a professional PDF with all selected information above and attach it to the email.</p>
        </div>
      </div>
      <div class="modal-footer glass-modal-footer">
        <button class="btn-secondary glass-btn" @click="close">Cancel</button>
        <button class="btn-primary glass-btn-primary" @click="sendEmail" :disabled="sending">
          <span v-if="sending">⏳ Sending...</span>
          <span v-else>📧 Send Email</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EmailModal',
  props: {
    visible: { type: Boolean, default: false },
    dispute: { type: Object, default: null },
    financeCompany: { type: Object, default: null },
    templates: { type: Array, required: true, default: () => [] },
    sending: { type: Boolean, default: false },
    orderHistoryData: { type: Object, default: null }
  },
  data() {
    return {
      selectedTemplateId: 'dispute_notification',
      subject: '',
      body: '',
      attachDisputeInfo: true,
      attachImagesAsAttachments: true,
      editingEmail: false,
      editableEmail: '',
      selectedEvidence: {
        customerInfo: true,
        orderItems: true,
        activityTimeline: true,
        deliveryInfo: true,
        routeInfo: true,
        deliveryProof: true
      }
    }
  },
  computed: {
    orderItemsCount() {
      const items = this.getOrderItems()
      return items ? items.length : 0
    },
    timelineEventsCount() {
      const history = this.getOrderHistory()
      return history ? history.length : 0
    },
    hasAnyEvidenceSelected() {
      return Object.values(this.selectedEvidence).some(v => v === true)
    }
  },
  watch: {
    visible: {
      handler(val) {
        if (val && this.templates.length) {
          this.selectTemplate(this.selectedTemplateId)
          this.selectedEvidence = {
            customerInfo: true,
            orderItems: true,
            activityTimeline: true,
            deliveryInfo: true,
            routeInfo: true,
            deliveryProof: true
          }
        }
        if (!val) {
          this.editingEmail = false
        }
      },
      immediate: true
    },
    financeCompany: {
      handler(val) {
        if (val && val.email) {
          this.editableEmail = val.email
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    if (this.templates.length) {
      this.selectTemplate(this.selectedTemplateId)
    }
  },
  methods: {
    getOrderHistory() {
      if (this.orderHistoryData && this.orderHistoryData.history) {
        return this.orderHistoryData.history
      }
      return []
    },
    getOrderItems() {
      const order = this.orderHistoryData && this.orderHistoryData.order
      if (order && order.Items) {
        return order.Items
      }
      return []
    },
    getCustomerInfo() {
      const order = this.orderHistoryData && this.orderHistoryData.order
      const customer = order && order.Customer ? order.Customer : {}
      return {
        name: customer.name || 'N/A',
        phone: customer.phone1 || 'N/A',
        email: customer.email || 'N/A',
        address: this.getCustomerAddress()
      }
    },
    getCustomerAddress() {
      const order = this.orderHistoryData && this.orderHistoryData.order
      const customer = order && order.Customer ? order.Customer : {}
      const parts = [customer.address1, customer.city, customer.state, customer.zipcode].filter(p => p)
      return parts.join(', ') || 'N/A'
    },
    getDeliveryInfo() {
      const order = this.orderHistoryData && this.orderHistoryData.order
      return {
        truckName: order && order.truck ? order.truck.truck_name : 'N/A',
        truckLicense: order && order.truck ? order.truck.license_no : 'N/A',
        dispatcherName: order && order.dispatcher ? order.dispatcher.name : 'N/A',
        tripNumber: order && order.trip ? order.trip.trip_no : 'N/A',
        contractorName: this.getContractorName()
      }
    },
    getContractorName() {
      const order = this.orderHistoryData && this.orderHistoryData.order
      const dispatcher = order && order.dispatcher ? order.dispatcher : null
      const contractor = dispatcher && dispatcher.contractor ? dispatcher.contractor : null
      return contractor && contractor.name ? contractor.name : 'N/A'
    },
    getRouteInfo() {
      const order = this.orderHistoryData && this.orderHistoryData.order
      const route = order && order.Route ? order.Route : null
      return route || null
    },
    getDeliveryProof() {
      const order = this.orderHistoryData && this.orderHistoryData.order
      return {
        signature: order && order.receiver_signature ? order.receiver_signature : null,
        signaturePath: order && order.receiver_signature_path ? order.receiver_signature_path : null,
        images: order && order.images ? order.images : []
      }
    },
    getDeliveryImages() {
      const proof = this.getDeliveryProof()
      return proof.images || []
    },
    getOrderTotalAmount() {
      const order = this.orderHistoryData && this.orderHistoryData.order
      const amount = order ? order.total_amount : 0
      return parseFloat(amount || 0).toFixed(2)
    },

    selectTemplate(templateId) {
      this.selectedTemplateId = templateId
      const template = this.templates.find(t => t.id === templateId)
      if (template) {
        this.subject = template.subject
        this.body = template.body
      }
    },
    formatText(command) {
      const textarea = this.$refs.bodyEditor
      if (!textarea) return
      
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const selectedText = this.body.substring(start, end)
      
      let replacement = ''
      if (command === 'bold') replacement = `**${selectedText}**`
      else if (command === 'italic') replacement = `*${selectedText}*`
      else if (command === 'underline') replacement = `__${selectedText}__`
      
      this.body = this.body.substring(0, start) + replacement + this.body.substring(end)
      
      this.$nextTick(() => {
        textarea.focus()
        textarea.setSelectionRange(start + replacement.length, start + replacement.length)
      })
    },
    insertPlaceholder(placeholder) {
      const textarea = this.$refs.bodyEditor
      if (!textarea) return
      
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      
      this.body = this.body.substring(0, start) + placeholder + this.body.substring(end)
      
      this.$nextTick(() => {
        textarea.focus()
        textarea.setSelectionRange(start + placeholder.length, start + placeholder.length)
      })
    },
    selectAllEvidence() {
      this.selectedEvidence = {
        customerInfo: true,
        orderItems: true,
        activityTimeline: true,
        deliveryInfo: true,
        routeInfo: true,
        deliveryProof: true
      }
    },
    deselectAllEvidence() {
      this.selectedEvidence = {
        customerInfo: false,
        orderItems: false,
        activityTimeline: false,
        deliveryInfo: false,
        routeInfo: false,
        deliveryProof: false
      }
    },
    startEditEmail() {
      this.editableEmail = this.financeCompany.email || ''
      this.editingEmail = true
      
      this.$nextTick(() => {
        const input = this.$el.querySelector('.email-input')
        if (input) input.focus()
      })
    },
    cancelEditEmail() {
      this.editingEmail = false
      this.editableEmail = ''
    },
    saveEmail() {
      if (this.financeCompany && this.editableEmail && this.editableEmail !== this.financeCompany.email) {
        this.$emit('update-email', {
          companyId: this.financeCompany.finance_company_id,
          email: this.editableEmail
        })
        this.financeCompany.email = this.editableEmail
      }
      this.editingEmail = false
    },
    close() {
      this.editingEmail = false
      this.$emit('close')
    },
    sendEmail() {
      if (!this.hasAnyEvidenceSelected && !this.attachDisputeInfo) {
        alert('Please select at least one information section to include in the email.')
        return
      }
      
      // Get image URLs for attachments
      const imageAttachments = this.attachImagesAsAttachments ? this.getImageAttachmentUrls() : []
      
      console.log('=== SENDING EMAIL ===')
      console.log('Image Attachments Count:', imageAttachments.length)
      
      // Create payload - backend will generate the PDF
      const payload = {
        to_email: this.financeCompany ? this.financeCompany.email : null,
        to_name: this.financeCompany ? this.financeCompany.finance_company_name : null,
        subject: this.subject,
        body: this.body,
        dispute_id: this.dispute ? this.dispute.id : null,
        invoice_id: this.dispute ? this.dispute.invoice_id : null,
        finance_company_id: this.financeCompany ? this.financeCompany.finance_company_id : null,
        notes: `Email sent regarding dispute #${this.dispute ? this.dispute.id : ''}`,
        sent_by: 'Admin',
        attach_dispute_info: this.attachDisputeInfo,
        selected_evidence: this.selectedEvidence,
        // Send order history data for backend to build PDF
        order_history: this.orderHistoryData || {},
        dispute_data: this.dispute || {},
        // Send image attachments for embedding in PDF and as separate files
        imageAttachments: imageAttachments
      }
      
      console.log('Payload keys:', Object.keys(payload))
      console.log('Order history available:', !!payload.order_history)
      console.log('Image attachments:', payload.imageAttachments.length)
      
      // Emit the event
      this.$emit('send', payload)
    },

    getImageAttachmentUrls() {
      const images = this.getDeliveryImages()
      const urls = []
      
      console.log('Getting image attachments, found:', images.length)
      
      images.forEach((img, idx) => {
        const imageUrl = img.image_path || img.url || img.image || ''
        if (imageUrl) {
          const fullUrl = this.getAbsoluteUrl(imageUrl)
          const urlParts = fullUrl.split('/')
          let filename = urlParts[urlParts.length - 1] || `delivery_image_${idx + 1}.jpg`
          
          if (!filename.includes('.')) {
            filename = `delivery_image_${idx + 1}.jpg`
          }
          
          urls.push({
            url: fullUrl,
            filename: filename,
            contentId: `image_${idx + 1}`
          })
          
          console.log(`Image ${idx + 1} URL:`, fullUrl)
        }
      })
      
      // Also add signature if available
      const proof = this.getDeliveryProof()
      if (proof.signaturePath || proof.signature) {
        const sigUrl = proof.signaturePath || proof.signature
        if (sigUrl) {
          const fullSigUrl = this.getAbsoluteUrl(sigUrl)
          const urlParts = fullSigUrl.split('/')
          let filename = urlParts[urlParts.length - 1] || 'signature.png'
          
          if (!filename.includes('.')) {
            filename = 'signature.png'
          }
          
          urls.push({
            url: fullSigUrl,
            filename: filename,
            contentId: 'signature'
          })
          
          console.log('Signature URL:', fullSigUrl)
        }
      }
      
      console.log('Total image attachments:', urls.length)
      return urls
    },

    getAbsoluteUrl(path) {
      if (!path) return ''
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path
      }
      const baseUrl = process.env.VUE_APP_API_URL || window.location.origin
      return `${baseUrl}/${path.replace(/^\/+/, '')}`
    },

    handleImageError(e) {
      console.error('Image failed to load:', e.target.src)
    }
  }
}
</script>

<style scoped>
/* All styles from before */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow-y: auto;
  padding: 20px;
}

.modal-container {
  width: 100%;
  max-width: 850px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: modalSlideIn 0.3s ease;
  margin: auto;
  background: white;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

@keyframes modalSlideIn {
  from { transform: translateY(-30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  flex-shrink: 0;
  border-bottom: 1px solid #e2e8f0;
  background: white;
  border-radius: 24px 24px 0 0;
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #94a3b8;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #f1f5f9;
  color: #475569;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
  border-top: 1px solid #e2e8f0;
  background: white;
  border-radius: 0 0 24px 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
}

.template-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
}

.template-btn {
  padding: 8px 16px;
  font-size: 13px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.template-btn.active {
  background: #1e293b;
  color: white;
  border-color: #1e293b;
}

.info-card {
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.info-card h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 10px;
}

.editable-field p {
  margin: 5px 0;
}

.email-display {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.edit-email-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 12px;
  color: #3b82f6;
}

.email-edit-mode {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.email-input {
  width: 250px;
  padding: 6px 10px;
  font-size: 13px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}

.save-email-btn, .cancel-email-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
}

.save-email-btn { color: #10b981; }
.cancel-email-btn { color: #ef4444; }

.email-editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
  padding: 8px;
  background: #f8fafc;
  border-radius: 8px;
}

.toolbar-btn {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.toolbar-btn:hover {
  background: #f1f5f9;
}

.modal-input, .modal-textarea {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  font-family: inherit;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
}

.modal-input:focus, .modal-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.modal-textarea {
  resize: vertical;
  font-family: monospace;
  line-height: 1.5;
}

.evidence-selection-group {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  margin: 20px 0;
  border: 1px solid #e2e8f0;
}

.image-attachments-group {
  background: #f0fdf4;
  border-radius: 12px;
  padding: 16px;
  margin: 20px 0;
  border: 1px solid #bbf7d0;
}

.section-label {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  display: block;
}

.evidence-options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
}

.evidence-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid #e2e8f0;
}

.evidence-checkbox:hover {
  background: #f1f5f9;
}

.evidence-checkbox input {
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin: 0;
}

.checkbox-content {
  font-size: 13px;
  color: #334155;
}

.selection-actions {
  display: flex;
  gap: 16px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.action-link {
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
}

.action-link:hover {
  background: #dbeafe;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: normal;
}

.checkbox-label input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.file-hint {
  font-size: 11px;
  color: #64748b;
  margin-top: 6px;
}

.image-preview-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.preview-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.preview-thumb {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.preview-name {
  font-size: 10px;
  color: #64748b;
}

.no-images-msg {
  padding: 12px;
  color: #64748b;
  font-size: 13px;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 20px;
  cursor: pointer;
}

.btn-primary {
  background: linear-gradient(135deg, #1e293b, #0f172a);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 24px;
  cursor: pointer;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .modal-container { max-width: 95%; }
  .evidence-options-grid { grid-template-columns: 1fr; }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/DisputedOrders/components/EmailModal.vue